import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/shared/search-input";
import { SearchAutocomplete } from "@/components/shared/search-autocomplete";
import { Bookmark, Share2, Clock, ArrowUpRight } from "lucide-react";
import type { HomepageArticle } from "@/components/homepage/types";

interface EditorialHeroSectionProps {
  featured: HomepageArticle | null;
  trendingTopics: Array<{ label: string; href: string }>;
  popularTags: string[];
  isLoading: boolean;
}

export function EditorialHeroSection({ featured, trendingTopics, popularTags, isLoading }: EditorialHeroSectionProps) {
  if (isLoading) {
    return (
      <section className="border-b border-border bg-neutral-50" aria-label="Editorial hero">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
          <div className="grid gap-10 lg:grid-cols-5">
            <div className="lg:col-span-3 space-y-6">
              <div className="h-4 w-24 bg-neutral-100 rounded animate-pulse" />
              <div className="h-10 w-full bg-neutral-100 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-neutral-100 rounded animate-pulse" />
              <div className="h-10 w-40 bg-neutral-100 rounded animate-pulse" />
            </div>
            <div className="lg:col-span-2">
              <div className="aspect-[3/4] bg-neutral-100 rounded-xl animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!featured) return null;

  return (
    <section className="border-b border-border bg-neutral-50" aria-label="Editorial hero">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div className="eyebrow flex items-center gap-2 mb-6">
              <span className="h-3.5 w-3.5 text-primary" aria-hidden="true">◆</span>
              Editor&apos;s Feature
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-balance text-foreground">
              {featured.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {typeof featured.content === "string" ? featured.content.slice(0, 250) : ""}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button size="lg" asChild>
                <Link href={`/news/${featured.id}`}>Read the story</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link href="/discover">
                  Explore more <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="mt-10 pt-8 border-t border-border">
              <h4 className="eyebrow mb-4">Trending Topics</h4>
              <div className="flex flex-wrap gap-2">
                {trendingTopics.map((topic) => (
                  <Link key={topic.label} href={topic.href}>
                    <span className="inline-flex items-center rounded-full border border-border bg-neutral-50 px-3 py-1 text-xs text-muted-foreground hover:bg-neutral-100 transition-colors cursor-pointer">
                      {topic.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <Link href={`/news/${featured.id}`} className="group block">
              <div className="aspect-[3/4] bg-neutral-100 rounded-xl overflow-hidden border border-border/50 mb-5">
                <div className="w-full h-full flex items-center justify-center text-muted-foreground/20 bg-neutral-50">
                  <span className="text-4xl" aria-hidden="true">📖</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" aria-hidden="true" />{featured.readTime ?? "8 min read"}</span>
                </div>
                <h3 className="text-lg font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors duration-200 line-clamp-2">
                  {featured.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                  <span className="font-medium text-foreground">{featured.author}</span>
                  <span className="text-neutral-300" aria-hidden="true">·</span>
                  <span>{featured.createdAt}</span>
                </div>
              </div>
            </Link>
            <div className="mt-6 flex items-center gap-2">
              <Button variant="ghost" size="sm" asChild className="gap-1">
                <Link href={`/news/${featured.id}`}>
                  <Bookmark className="h-3.5 w-3.5" aria-hidden="true" /> Save
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="gap-1">
                <Link href={`/news/${featured.id}`}>
                  <Share2 className="h-3.5 w-3.5" aria-hidden="true" /> Share
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h4 className="eyebrow">Popular Tags</h4>
            <SearchInput />
          </div>
          <div className="flex flex-wrap gap-2">
            {popularTags.map((tag) => (
              <Link key={tag} href={`/search?q=${encodeURIComponent(tag)}`}>
                <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground hover:bg-neutral-100 transition-colors cursor-pointer">
                  {tag}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}